import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, onSnapshot, collection, query, where, getDocs, deleteDoc, orderBy, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { db } from '../firebase';

// Unified status function (same as AdminDashboard)
const getUnifiedStatus = (app) => {
  const phase = app.currentPhase || 1;
  const status = app.status;
  
  switch (phase) {
    case 1:
      if (status === 'pending') return 'Phase 1 Submitted';
      if (status === 'auto_approved') return 'Phase 1 Approved - Auto';
      if (status === 'approved_phase2') return 'Phase 1 Approved - Manual';
      if (status === 'manual_review') return 'Phase 1 Pending Approval';
      if (status === 'auto_rejected' || status === 'rejected_manual') return 'Phase 1 Failed';
      return 'Phase 1 Submitted';
    
    case 2:
      if (status === 'phase2_invited' || status === 'approved_phase2') return 'Phase 2 Invited';
      return 'Phase 2 In Progress';
    
    case 3:
      if (status === 'phase3_invited') return 'Phase 3 Invited';
      if (status === 'phase3_submitted') return 'Phase 3 Submitted and Pending Approval';
      if (status === 'phase3_pending') return 'Phase 3 Submitted and Pending Approval';
      if (status === 'phase3_approved') return 'Phase 3 Approved - Auto';
      if (status === 'phase3_approved_manual') return 'Phase 3 Approved - Manual';
      if (status === 'phase3_failed') return 'Phase 3 Failed';
      return 'Phase 3 Submitted and Pending Approval';
    
    case 4:
      if (status === 'phase4_invited') return 'Phase 4 Invited';
      if (status === 'phase4_accepted') return 'Phase 4 Accepted';
      if (status === 'phase4_failed') return 'Phase 4 Failed';
      return 'Phase 4 Interview';
    
    default:
      return 'Phase 1 Submitted';
  }
};

const CandidateProfile = ({ candidateId, onClose }) => {
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'entries' or 'user'
  const [deleting, setDeleting] = useState(false);
  const [phase3Data, setPhase3Data] = useState(null);
  const [showPhase4Modal, setShowPhase4Modal] = useState(false);
  const [interviewers, setInterviewers] = useState([]);
  const [selectedInterviewer, setSelectedInterviewer] = useState('');
  const [phase4DocumentInput, setPhase4DocumentInput] = useState('');

  useEffect(() => {
    if (!candidateId) return;

    const unsubscribe = onSnapshot(doc(db, 'applications', candidateId), async (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setCandidate({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        });
        setAdminNotes(data.adminNotes || '');

        // Also check for separate Phase 3 data (temporary migration support)
        try {
          const phase3Query = query(
            collection(db, 'phase3Applications'),
            where('userId', '==', data.userId)
          );
          const phase3Snapshot = await getDocs(phase3Query);
          if (!phase3Snapshot.empty) {
            const phase3Doc = phase3Snapshot.docs[0].data();
            setPhase3Data(phase3Doc);
          }
        } catch (error) {
          console.error('Error loading Phase 3 data:', error);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [candidateId]);

  const saveAdminNotes = async () => {
    setSavingNotes(true);
    try {
      await updateDoc(doc(db, 'applications', candidateId), {
        adminNotes: adminNotes,
        lastNoteUpdate: new Date()
      });
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  // Load interviewers when opening Phase 4 modal
  const openPhase4ApprovalModal = async () => {
    setShowPhase4Modal(true);
    try {
      const interviewersQuery = query(collection(db, 'interviewers'), orderBy('name'));
      const interviewersSnapshot = await getDocs(interviewersQuery);
      const interviewersData = interviewersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInterviewers(interviewersData);
    } catch (error) {
      console.error('Error loading interviewers:', error);
    }
  };

  // Approve Phase 3 and assign interviewer
  const approvePhase4WithInterviewer = async () => {
    if (!selectedInterviewer) {
      alert('Please select an interviewer');
      return;
    }

    try {
      const interviewer = interviewers.find(i => i.id === selectedInterviewer);
      
      // Update candidate status
      await updateDoc(doc(db, 'applications', candidateId), {
        currentPhase: 4,
        status: 'phase4_assigned',
        assignedInterviewer: {
          id: interviewer.id,
          name: interviewer.name,
          email: interviewer.email,
          title: interviewer.title,
          calendarLink: interviewer.calendarLink
        },
        phase4AssignedAt: serverTimestamp(),
        emailSent: false, // Reset email status for Phase 4 emails
        updatedAt: serverTimestamp()
      });

      setShowPhase4Modal(false);
      setSelectedInterviewer('');
      
      alert(`Successfully assigned ${interviewer.name} as interviewer! Email will be sent separately.`);
      
      // Refresh candidate data
      const updatedDoc = await getDoc(doc(db, 'applications', candidateId));
      if (updatedDoc.exists()) {
        setCandidate(updatedDoc.data());
      }
    } catch (error) {
      console.error('Error assigning interviewer:', error);
      alert('Failed to assign interviewer: ' + error.message);
    }
  };

  const handleDeleteEntries = async () => {
    setDeleting(true);
    try {
      // Delete main application document
      await deleteDoc(doc(db, 'applications', candidateId));
      
      // Delete any Phase 3 application documents
      const phase3Query = query(
        collection(db, 'phase3Applications'),
        where('userId', '==', candidate.userId)
      );
      const phase3Snapshot = await getDocs(phase3Query);
      
      const deletePromises = phase3Snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      alert('All application entries have been deleted successfully.');
      onClose();
    } catch (error) {
      console.error('Error deleting entries:', error);
      alert('Failed to delete entries. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteUser = async () => {
    setDeleting(true);
    try {
      // Call the Cloud Function to delete user completely
      const deleteUser = httpsCallable(functions, 'deleteUser');
      const result = await deleteUser({ userId: candidate.userId });
      
      if (result.data.success) {
        alert(`User and all associated data deleted successfully!\n\n${result.data.message}\n\nDocuments removed: ${result.data.deletedDocuments}\n\nThe user can now recreate their account if needed.`);
        onClose();
      } else {
        throw new Error(result.data.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(`Failed to delete user completely: ${error.message}\n\nPlease try again or contact support.`);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const confirmDelete = () => {
    if (deleteType === 'entries') {
      handleDeleteEntries();
    } else if (deleteType === 'user') {
      handleDeleteUser();
    }
  };

  const updatePhaseStatus = async (newPhase, newStatus) => {
    try {
      const updateData = {
        currentPhase: newPhase,
        status: newStatus,
        [`phase${newPhase}CompletedAt`]: new Date(),
        updatedAt: new Date()
      };

      // If advancing to Phase 2, add to email queue
      if (newPhase === 2 && (newStatus === 'phase2_invited' || newStatus === 'approved_phase2')) {
        updateData.emailQueue = true;
        updateData.addedToQueueAt = new Date();
        updateData.emailSent = false;
      }
      
      // If rejecting Phase 3, reset email status for Phase 4 rejection emails
      if (newStatus === 'phase3_rejected') {
        updateData.emailSent = false;
      }

      await updateDoc(doc(db, 'applications', candidateId), updateData);
    } catch (error) {
      console.error('Error updating phase:', error);
    }
  };

  const getPhaseProgress = () => {
    const currentPhase = candidate?.currentPhase || 1;
    const phases = [
      { number: 1, name: 'Initial Application', status: currentPhase >= 1 ? 'completed' : 'pending' },
      { number: 2, name: 'Group Session', status: currentPhase >= 2 ? 'completed' : currentPhase === 1 ? 'next' : 'pending' },
      { number: 3, name: 'In-Depth Application', status: currentPhase >= 3 ? 'completed' : currentPhase === 2 ? 'next' : 'pending' },
      { number: 4, name: 'Interview', status: currentPhase >= 4 ? 'completed' : currentPhase === 3 ? 'next' : 'pending' }
    ];
    return phases;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'auto_approved': 'bg-green-100 text-green-800',
      'approved_phase2': 'bg-green-100 text-green-800',
      'approved_phase3': 'bg-green-100 text-green-800',
      'approved_final': 'bg-green-100 text-green-800',
      'auto_rejected': 'bg-red-100 text-red-800',
      'rejected_manual': 'bg-red-100 text-red-800',
      'manual_review': 'bg-blue-100 text-blue-800',
      'phase2_invited': 'bg-purple-100 text-purple-800',
      'phase3_invited': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Phase 4 helper functions
  const updateInterviewScheduled = async (scheduled) => {
    try {
      await updateDoc(doc(db, 'applications', candidateId), {
        phase4InterviewScheduled: scheduled,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating interview scheduled status:', error);
      alert('Failed to update interview status');
    }
  };

  const setPhase4Document = async () => {
    if (!phase4DocumentInput.trim()) {
      alert('Please enter a document URL');
      return;
    }

    try {
      await updateDoc(doc(db, 'applications', candidateId), {
        phase4DocumentUrl: phase4DocumentInput.trim(),
        updatedAt: serverTimestamp()
      });
      setPhase4DocumentInput('');
      alert('Document URL set successfully');
    } catch (error) {
      console.error('Error setting document URL:', error);
      alert('Failed to set document URL');
    }
  };

  const resetPhase4Document = async () => {
    if (!confirm('Are you sure you want to remove the document URL?')) {
      return;
    }

    try {
      await updateDoc(doc(db, 'applications', candidateId), {
        phase4DocumentUrl: null,
        updatedAt: serverTimestamp()
      });
      alert('Document URL removed successfully');
    } catch (error) {
      console.error('Error removing document URL:', error);
      alert('Failed to remove document URL');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading candidate profile...</p>
        </div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Candidate Not Found</h3>
          <button 
            onClick={onClose}
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {candidate.firstName} {candidate.lastName}
            </h2>
            <div className="flex items-center space-x-4 mt-2">
              <span className="text-sm text-gray-500">
                Phase {candidate.currentPhase || 1} • {getUnifiedStatus(candidate)}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Delete Options */}
            <div className="relative">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-600 hover:text-red-800 p-2 rounded-md hover:bg-red-50"
                title="Delete Options"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Phase Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Application Overview
              </button>
              <button
                onClick={() => setActiveTab('phase1')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'phase1'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Phase 1 Application
              </button>
              {candidate.currentPhase >= 3 && (
                <button
                  onClick={() => setActiveTab('phase3')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'phase3'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Phase 3 Application
                </button>
              )}
              {candidate.currentPhase >= 4 && (
                <button
                  onClick={() => setActiveTab('phase4')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'phase4'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Phase 4 Interview
                </button>
              )}
            </nav>
          </div>
        </div>

        {/* Application Overview Content */}
        {activeTab === 'overview' && (
          <>
            {/* Progress Status Bar */}
            <div className="mb-8 bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Application Progress</h3>
              <div className="flex items-center justify-between">
                {getPhaseProgress().map((phase, index) => (
                  <div key={phase.number} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                        phase.status === 'completed' ? 'bg-green-600 text-white' :
                        phase.status === 'next' ? 'bg-blue-600 text-white' :
                        'bg-gray-300 text-gray-600'
                      }`}>
                        {phase.number}
                      </div>
                      <span className="text-xs text-center mt-2 max-w-20">{phase.name}</span>
                    </div>
                    {index < getPhaseProgress().length - 1 && (
                      <div className={`flex-1 h-1 mx-4 ${
                        phase.status === 'completed' ? 'bg-green-600' : 'bg-gray-300'
                      }`}></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Current Status */}
            <div className="mb-6 flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">Current Status:</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(candidate.status)}`}>
                {getUnifiedStatus(candidate)}
              </span>
              <span className="text-sm text-gray-500">
                Phase {candidate.currentPhase || 1}
              </span>
            </div>


            {/* Phase Actions */}
            <div className="mb-6 bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Phase Actions</h4>
              <div className="flex flex-wrap gap-2">
                {candidate.status === 'manual_review' && (
                  <>
                    <button
                      onClick={() => updatePhaseStatus(2, 'phase2_invited')}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                    >
                      Invite to Phase 2
                    </button>
                    <button
                      onClick={() => updatePhaseStatus(1, 'rejected_manual')}
                      className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                    >
                      Reject Phase 1
                    </button>
                  </>
                )}
                
                {candidate.currentPhase === 2 && (
                  <button
                    onClick={() => updatePhaseStatus(3, 'phase3_invited')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Invite to Phase 3
                  </button>
                )}
                
                {(candidate.status === 'phase3_submitted' || candidate.status === 'phase3_pending') && (
                  <>
                    <button
                      onClick={() => openPhase4ApprovalModal()}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                    >
                      Invite to Phase 4 Interview
                    </button>
                    <button
                      onClick={() => updatePhaseStatus(3, 'phase3_rejected')}
                      className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                    >
                      Reject Phase 3
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
              <p><strong>Applied:</strong> {candidate.createdAt?.toLocaleDateString()}</p>
              <p><strong>Last Updated:</strong> {candidate.updatedAt?.toLocaleDateString()}</p>
              <p><strong>How They Heard:</strong> {candidate.howHeardAbout}</p>
              <p><strong>Can Commit:</strong> {candidate.canCommit ? 'Yes' : 'No'}</p>
            </div>
            
            {/* Notes Section */}
            <div className="mt-8 bg-white border rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                Notes
              </h4>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                placeholder="Add notes about this candidate..."
              />
              <button
                onClick={saveAdminNotes}
                disabled={savingNotes}
                className="mt-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {savingNotes ? 'Saving...' : 'Save Notes'}
              </button>
            </div>
          </>
        )}

        {/* Phase 1 Content */}
        {activeTab === 'phase1' && (
          <>
            {/* Phase 1 Application Form Display */}
            <div className="space-y-6">
              {/* Personal Information Section */}
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                  Personal Information
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-bold text-gray-700">First Name</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.firstName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-bold text-gray-700">Last Name</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.lastName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-bold text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-bold text-gray-700">Phone</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.phone}</p>
                  </div>
                  <div>
                    <div className="flex items-center">
                      {candidate.yellowFlags?.some(flag => flag.includes('work email')) && (
                        <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                        </svg>
                      )}
                      <label className="text-sm font-bold text-gray-700">Work Email</label>
                    </div>
                    <p className="mt-1 text-sm text-gray-900">{candidate.workEmail || 'Not provided'}</p>
                  </div>
                  <div>
                    <div className="flex items-center">
                      {candidate.yellowFlags?.some(flag => flag.includes('LinkedIn')) && (
                        <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                        </svg>
                      )}
                      <label className="text-sm font-bold text-gray-700">LinkedIn Profile</label>
                    </div>
                    <p className="mt-1 text-sm">
                      {candidate.linkedin ? (
                        <a href={candidate.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          View Profile
                        </a>
                      ) : (
                        <span className="text-gray-500">Not provided</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Company Information Section */}
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                  Company Information
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-bold text-gray-700">Company Name</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.companyName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-bold text-gray-700">Role in Company</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.role}</p>
                  </div>
                  <div>
                    <div className="flex items-center">
                      {candidate.yellowFlags?.some(flag => flag.includes('Applicant role is not founder')) && (
                        <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                        </svg>
                      )}
                      <label className="text-sm font-bold text-gray-700">Are you a founder?</label>
                    </div>
                    <p className="mt-1 text-sm text-gray-900">
                      {candidate.isFounder === true ? 'Yes' : candidate.isFounder === false ? 'No' : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center">
                      {candidate.yellowFlags?.some(flag => flag.includes('website')) && (
                        <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                        </svg>
                      )}
                      <label className="text-sm font-bold text-gray-700">Company Website</label>
                    </div>
                    <p className="mt-1 text-sm">
                      {candidate.companyWebsite ? (
                        <a href={candidate.companyWebsite} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          {candidate.companyWebsite}
                        </a>
                      ) : (
                        <span className="text-gray-500">Not provided</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center">
                      {candidate.yellowFlags?.some(flag => flag.includes('spelling') || flag.includes('grammar')) && (
                        <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                        </svg>
                      )}
                      <label className="text-sm font-bold text-gray-700">Company Description ("Grandma Test")</label>
                    </div>
                    <div className="mt-2 p-3 bg-gray-50 rounded-md">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.grandmaTest}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Military Service Section */}
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                  Military Service
                </h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center">
                      {candidate.redFlags?.some(flag => flag.includes('Did not serve in USA or Israel')) && (
                        <svg className="w-4 h-4 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                        </svg>
                      )}
                      <label className="text-sm font-bold text-gray-700">Country Served</label>
                    </div>
                    <p className="mt-1 text-sm text-gray-900">{candidate.countryServed?.toUpperCase()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-bold text-gray-700">Unit Served</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.unitServed}</p>
                  </div>
                  <div>
                    <label className="text-sm font-bold text-gray-700">Role in Service</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.roleInService}</p>
                  </div>
                  <div>
                    <label className="text-sm font-bold text-gray-700">Service Start Year</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.serviceStartYear}</p>
                  </div>
                  <div>
                    <label className="text-sm font-bold text-gray-700">Service End Year</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.serviceEndYear}</p>
                  </div>
                  <div>
                    <div className="flex items-center">
                      {candidate.yellowFlags?.some(flag => flag.includes('combat role')) && (
                        <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                        </svg>
                      )}
                      <label className="text-sm font-bold text-gray-700">Combat Role</label>
                    </div>
                    <p className="mt-1 text-sm text-gray-900">{candidate.combatRole === 'yes' ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>

              {/* Pitch Deck Section */}
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                  Pitch Deck
                </h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center">
                      {candidate.yellowFlags?.some(flag => flag.includes('pitch deck')) && (
                        <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                        </svg>
                      )}
                      <label className="text-sm font-bold text-gray-700">Pitch Deck Upload</label>
                    </div>
                    <p className="mt-1 text-sm">
                      {candidate.deckUrl ? (
                        <a href={candidate.deckUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          View Pitch Deck
                        </a>
                      ) : (
                        <span className="text-gray-500">No pitch deck uploaded</span>
                      )}
                    </p>
                  </div>
                  {candidate.noDeckReason && (
                    <div>
                      <label className="text-sm font-bold text-gray-700">Reason for No Pitch Deck</label>
                      <div className="mt-2 p-3 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.noDeckReason}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information Section */}
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                  Additional Information
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-bold text-gray-700">How did you hear about this program?</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.howHeardAbout}</p>
                  </div>
                  <div>
                    <div className="flex items-center">
                      {candidate.yellowFlags?.some(flag => flag.includes('Cannot commit to full program')) && (
                        <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                        </svg>
                      )}
                      <label className="text-sm font-bold text-gray-700">Can you commit to the full program?</label>
                    </div>
                    <p className="mt-1 text-sm text-gray-900">{candidate.canCommit ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>

              {/* Flags Summary */}
              {(candidate.redFlags?.length > 0 || candidate.yellowFlags?.length > 0) && (
                <div className="bg-white border rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                    Phase 1 Flags
                  </h4>
                  
                  {candidate.redFlags?.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 mr-2">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                          </svg>
                          Red Flags ({candidate.redFlags.length})
                        </span>
                        <span className="text-sm text-gray-500">Critical Issues</span>
                      </div>
                      <div className="space-y-1 ml-4">
                        {candidate.redFlags.map((flag, idx) => (
                          <div key={idx} className="text-red-700 text-sm">• {flag}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {candidate.yellowFlags?.length > 0 && (
                    <div>
                      <div className="flex items-center mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mr-2">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                          </svg>
                          Yellow Flags ({candidate.yellowFlags.length})
                        </span>
                        <span className="text-sm text-gray-500">Requires Review</span>
                      </div>
                      <div className="space-y-1 ml-4">
                        {candidate.yellowFlags.map((flag, idx) => (
                          <div key={idx} className="text-yellow-700 text-sm">• {flag}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>
            
            {/* Notes Section */}
            <div className="mt-8 bg-white border rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                Notes
              </h4>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                placeholder="Add notes about this candidate..."
              />
              <button
                onClick={saveAdminNotes}
                disabled={savingNotes}
                className="mt-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {savingNotes ? 'Saving...' : 'Save Notes'}
              </button>
            </div>
          </>
        )}

        {/* Phase 3 Content */}
        {activeTab === 'phase3' && candidate.currentPhase >= 3 && (
          <div className="space-y-6">
            {/* Phase 3 Header */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Phase 3: In-Depth Application</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><strong>Phase 3 Started:</strong> {candidate.phase3SubmittedAt ? (candidate.phase3SubmittedAt.toDate ? candidate.phase3SubmittedAt.toDate().toLocaleDateString() : new Date(candidate.phase3SubmittedAt).toLocaleDateString()) : 'In Progress'}</div>
                <div><strong>Status:</strong> 
                  <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(candidate.status)}`}>
                    {getUnifiedStatus(candidate)}
                  </span>
                </div>
              </div>
            </div>

            {/* Phase 3 Flags */}
            {candidate.currentPhase >= 3 && (candidate.productStage || candidate.tractionDetails || candidate.videoPitchUrl || candidate.capTable || candidate.capTableData || phase3Data) && (() => {
              const dataSource = candidate.productStage || candidate.tractionDetails || candidate.videoPitchUrl || candidate.capTable || candidate.capTableData ? candidate : phase3Data;
              return (dataSource.phase3RedFlags?.length > 0 || dataSource.phase3YellowFlags?.length > 0) && (
                <div className="bg-white border rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                    Phase 3 Flags
                  </h4>
                  
                  {dataSource.phase3RedFlags?.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 mr-2">
                          🔴 Red Flags ({dataSource.phase3RedFlags.length})
                        </span>
                        <span className="text-sm text-gray-500">Critical Issues</span>
                      </div>
                      <div className="space-y-1 ml-4">
                        {dataSource.phase3RedFlags.map((flag, idx) => (
                          <div key={idx} className="text-red-700 text-sm">• {flag}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {dataSource.phase3YellowFlags?.length > 0 && (
                    <div>
                      <div className="flex items-center mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mr-2">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                          </svg>
                          Yellow Flags ({dataSource.phase3YellowFlags.length})
                        </span>
                        <span className="text-sm text-gray-500">Requires Review</span>
                      </div>
                      <div className="space-y-1 ml-4">
                        {dataSource.phase3YellowFlags.map((flag, idx) => (
                          <div key={idx} className="text-yellow-700 text-sm">• {flag}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Check if Phase 3 data exists */}
            {candidate.currentPhase >= 3 && (candidate.productStage || candidate.tractionDetails || candidate.videoPitchUrl || candidate.capTable || candidate.capTableData || phase3Data) ? (
              <div>
                {/* Determine data source - unified record takes priority */}
                {(() => {
                  const dataSource = candidate.productStage || candidate.tractionDetails || candidate.videoPitchUrl || candidate.capTable || candidate.capTableData ? candidate : phase3Data;
                  if (!dataSource) return null;
                  
                  return (
                    <div className="space-y-6">
                      {phase3Data && !candidate.productStage && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                          <div className="text-yellow-800 text-sm">
                            📂 <strong>Separate Phase 3 Data Found:</strong> This data was submitted before the system upgrade. 
                            It should be migrated to the unified record.
                          </div>
                        </div>
                      )}
                      
                      {/* Business & Product Section */}
                      <div className="bg-white border rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                          Part 1: Business & Product
                        </h4>
                        <div className="space-y-4">
                          {dataSource.companyName && (
                            <div>
                              <label className="text-sm font-bold text-gray-700">Company Name</label>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.companyName}</p>
                            </div>
                          )}
                          {(dataSource.companyWebsite || candidate.companyWebsite) && (
                            <div>
                              <label className="text-sm font-bold text-gray-700">Company Website</label>
                              <p className="mt-1 text-sm">
                                <a href={dataSource.companyWebsite || candidate.companyWebsite} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                  {dataSource.companyWebsite || candidate.companyWebsite}
                                </a>
                              </p>
                            </div>
                          )}
                          {dataSource.incorporationLocation && (
                            <div>
                              <label className="text-sm font-bold text-gray-700">Incorporation Location</label>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.incorporationLocation}</p>
                            </div>
                          )}
                          {dataSource.productStage && (
                            <div>
                              <label className="text-sm font-bold text-gray-700">Product Stage</label>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.productStage}</p>
                            </div>
                          )}
                          {dataSource.videoPitchUrl && (
                            <div>
                              <label className="text-sm font-bold text-gray-700">Video Pitch</label>
                              <p className="mt-1">
                                <a href={dataSource.videoPitchUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                                  <i className="fas fa-play-circle mr-2"></i>
                                  Watch Video
                                </a>
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {dataSource.tractionDetails && (
                          <div className="mt-6">
                            <label className="text-sm font-bold text-gray-700">Traction Details</label>
                            <div className="mt-2 p-3 bg-gray-50 rounded-md">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{dataSource.tractionDetails}</p>
                            </div>
                          </div>
                        )}
                        
                        {dataSource.problemCustomer && (
                          <div className="mt-6">
                            <div className="flex items-center">
                              {dataSource.phase3YellowFlags?.some(flag => flag.includes('Problem & Customer')) && (
                                <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                </svg>
                              )}
                              <label className="text-sm font-bold text-gray-700">Problem & Customer</label>
                            </div>
                            <div className="mt-2 p-3 bg-gray-50 rounded-md">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{dataSource.problemCustomer}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Team Section */}
                      <div className="bg-white border rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                          Part 2: Team
                        </h4>
                        <div className="space-y-4">
                          {dataSource.capacity && (
                            <div>
                              <div className="flex items-center">
                                {dataSource.phase3YellowFlags?.some(flag => flag.includes('Part-time capacity')) && (
                                  <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                  </svg>
                                )}
                                <label className="text-sm font-bold text-gray-700">Capacity</label>
                              </div>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.capacity}</p>
                              {dataSource.partTimeExplanation && (
                                <div className="mt-2 p-2 bg-yellow-50 rounded">
                                  <p className="text-sm text-gray-700">{dataSource.partTimeExplanation}</p>
                                </div>
                              )}
                            </div>
                          )}
                          {dataSource.previousPartners && (
                            <div>
                              <div className="flex items-center">
                                {dataSource.phase3YellowFlags?.some(flag => flag.includes('Previous business partners departed')) && (
                                  <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                  </svg>
                                )}
                                <label className="text-sm font-bold text-gray-700">Previous Business Partners</label>
                              </div>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.previousPartners}</p>
                              {dataSource.departureExplanation && (
                                <div className="mt-2 p-2 bg-gray-50 rounded">
                                  <p className="text-sm text-gray-700">{dataSource.departureExplanation}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {dataSource.workedTogether && (
                          <div className="mt-4">
                            <label className="text-sm font-bold text-gray-700">Previous Working Relationships</label>
                            <div className="mt-2 p-3 bg-gray-50 rounded-md">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{dataSource.workedTogether}</p>
                            </div>
                          </div>
                        )}
                        
                        {dataSource.equitySplit && (
                          <div className="mt-4">
                            <label className="text-sm font-bold text-gray-700">Equity Split & Roles</label>
                            <div className="mt-2 p-3 bg-gray-50 rounded-md">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{dataSource.equitySplit}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Funding Section */}
                      <div className="bg-white border rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                          Part 3: Funding & Cap Table
                        </h4>
                        <div className="space-y-4">
                          {dataSource.hasRaised && (
                            <div>
                              <label className="text-sm font-bold text-gray-700">Has Raised Funding</label>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.hasRaised}</p>
                            </div>
                          )}
                          {dataSource.fundingStage && (
                            <div>
                              <label className="text-sm font-bold text-gray-700">Funding Stage</label>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.fundingStage}</p>
                            </div>
                          )}
                          {dataSource.fundingAmount && (
                            <div>
                              <label className="text-sm font-bold text-gray-700">Funding Amount</label>
                              <p className="mt-1 text-sm text-gray-900">${dataSource.fundingAmount?.toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Funding Details */}
                        {dataSource.fundingDetails && (
                          <div className="mt-4">
                            <label className="text-sm font-bold text-gray-700">Funding Details</label>
                            <div className="mt-2 p-3 bg-gray-50 rounded-md">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{dataSource.fundingDetails}</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Future Funding Plans */}
                        {dataSource.fundingPlans && (
                          <div className="mt-4">
                            <label className="text-sm font-bold text-gray-700">Future Funding Plans</label>
                            <div className="mt-2 p-3 bg-gray-50 rounded-md">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{dataSource.fundingPlans}</p>
                            </div>
                          </div>
                        )}

                        {/* Cap Table Display */}
                        {(() => {
                          const capTable = dataSource.capTable || dataSource.capTableData;
                          if (!capTable) return null;
                          
                          return (
                            <div className="mt-6">
                              <div className="flex justify-between items-center mb-3">
                                <div className="flex items-center">
                                  {dataSource.phase3YellowFlags?.some(flag => 
                                    flag.includes('Founder') || 
                                    flag.includes('More than 5 investors') || 
                                    flag.includes('Advisor') || 
                                    flag.includes('Inactive')
                                  ) && (
                                    <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                  <label className="text-sm font-bold text-gray-700">Cap Table</label>
                                </div>
                                {/* Yellow Flag Alert */}
                                {dataSource.phase3YellowFlags?.some(flag => flag.includes('Founder')) && (
                                  <div className="flex items-center">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                      ⚠️ Ownership Issues Detected
                                    </span>
                                  </div>
                                )}
                              </div>
                            
                              {/* Cap Table Summary */}
                              <div className="grid grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                                <div className="text-center">
                                  <div className="text-lg font-semibold text-gray-900">
                                    {(() => {
                                      const founderShares = capTable?.founders?.reduce((sum, f) => sum + (f.shares || 0), 0) || 0;
                                      const totalShares = capTable?.grandTotal?.shares || 
                                        (capTable?.founders?.reduce((sum, f) => sum + (f.shares || 0), 0) || 0) +
                                        (capTable?.employeePool?.shares || 0) +
                                        (capTable?.seedInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0) +
                                        (capTable?.seriesAInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0);
                                      return totalShares > 0 ? ((founderShares / totalShares) * 100).toFixed(1) + '%' : '0%';
                                    })()}
                                  </div>
                                  <div className="text-xs text-gray-600">Founders</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-lg font-semibold text-gray-900">
                                    {(() => {
                                      const employeeShares = capTable?.employeePool?.shares || 0;
                                      const totalShares = capTable?.grandTotal?.shares || 
                                        (capTable?.founders?.reduce((sum, f) => sum + (f.shares || 0), 0) || 0) +
                                        (capTable?.employeePool?.shares || 0) +
                                        (capTable?.seedInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0) +
                                        (capTable?.seriesAInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0);
                                      return totalShares > 0 ? ((employeeShares / totalShares) * 100).toFixed(1) + '%' : '0%';
                                    })()}
                                  </div>
                                  <div className="text-xs text-gray-600">Employee Pool</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-lg font-semibold text-gray-900">
                                    {(() => {
                                      const investorShares = (capTable?.seedInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0) +
                                        (capTable?.seriesAInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0);
                                      const totalShares = capTable?.grandTotal?.shares || 
                                        (capTable?.founders?.reduce((sum, f) => sum + (f.shares || 0), 0) || 0) +
                                        (capTable?.employeePool?.shares || 0) +
                                        (capTable?.seedInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0) +
                                        (capTable?.seriesAInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0);
                                      return totalShares > 0 ? ((investorShares / totalShares) * 100).toFixed(1) + '%' : '0%';
                                    })()}
                                  </div>
                                  <div className="text-xs text-gray-600">Investors</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-lg font-semibold text-gray-900">
                                    {(() => {
                                      const totalInvestors = (capTable?.seedInvestors?.length || 0) + 
                                        (capTable?.seriesAInvestors?.length || 0);
                                      return totalInvestors;
                                    })()}
                                  </div>
                                  <div className="text-xs text-gray-600">Total Investors</div>
                                </div>
                              </div>
                            
                              {/* Detailed Cap Table */}
                              <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Shares</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">%</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {/* Founders */}
                                    {capTable.founders?.map((founder, idx) => {
                                      const totalShares = capTable.grandTotal?.shares || 
                                        (capTable.founders?.reduce((sum, f) => sum + (f.shares || 0), 0) || 0) +
                                        (capTable.employeePool?.shares || 0) +
                                        (capTable.seedInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0) +
                                        (capTable.seriesAInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0);
                                      const percentage = totalShares > 0 ? ((founder.shares / totalShares) * 100).toFixed(1) : '0.0';
                                      
                                      // Check if this founder row should be highlighted
                                      const shouldHighlight = dataSource.phase3YellowFlags?.some(flag => 
                                        (flag.includes('Co-founders with drastically uneven ownership') && capTable.founders?.length > 1) ||
                                        (flag.includes('Single founder') && capTable.founders?.length === 1 && parseFloat(percentage) < 80) ||
                                        (flag.includes('Founder') && flag.includes('collectively own <70%'))
                                      );
                                      
                                      return (
                                        <tr key={idx} className={shouldHighlight ? 'bg-yellow-50' : ''}>
                                          <td className="px-4 py-2 text-sm text-gray-900 font-medium">{founder.name || `Founder ${idx + 1}`}</td>
                                          <td className="px-4 py-2 text-sm">
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                              Founder
                                            </span>
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{(founder.shares || 0).toLocaleString()}</td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{percentage}%</td>
                                        </tr>
                                      );
                                    })}
                                    
                                    {/* Employee Pool */}
                                    {capTable.employeePool?.shares > 0 && (
                                      <tr>
                                        <td className="px-4 py-2 text-sm text-gray-900 font-medium">Employee Pool</td>
                                        <td className="px-4 py-2 text-sm">
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            Employee Pool
                                          </span>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-900">{(capTable.employeePool.shares || 0).toLocaleString()}</td>
                                        <td className="px-4 py-2 text-sm text-gray-900">
                                          {(() => {
                                            const totalShares = capTable.grandTotal?.shares || 
                                              (capTable.founders?.reduce((sum, f) => sum + (f.shares || 0), 0) || 0) +
                                              (capTable.employeePool?.shares || 0) +
                                              (capTable.seedInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0) +
                                              (capTable.seriesAInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0);
                                            return totalShares > 0 ? (((capTable.employeePool.shares || 0) / totalShares) * 100).toFixed(1) + '%' : '0.0%';
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    
                                    {/* Seed Investors */}
                                    {capTable.seedInvestors?.filter(investor => investor.shares > 0).map((investor, idx) => {
                                      const totalShares = capTable.grandTotal?.shares || 
                                        (capTable.founders?.reduce((sum, f) => sum + (f.shares || 0), 0) || 0) +
                                        (capTable.employeePool?.shares || 0) +
                                        (capTable.seedInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0) +
                                        (capTable.seriesAInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0);
                                      const percentage = totalShares > 0 ? ((investor.shares / totalShares) * 100).toFixed(1) : '0.0';
                                      
                                      // Check if this investor row should be highlighted
                                      const shouldHighlight = dataSource.phase3YellowFlags?.some(flag => 
                                        (flag.includes('Advisor') && flag.includes('>5% equity') && parseFloat(percentage) > 5) ||
                                        (flag.includes('More than 5 investors') && 
                                         (capTable.seedInvestors?.length + capTable.seriesAInvestors?.length) > 5)
                                      );
                                      
                                      return (
                                        <tr key={idx} className={shouldHighlight ? 'bg-yellow-50' : ''}>
                                          <td className="px-4 py-2 text-sm text-gray-900">{investor.name || `Seed Investor ${idx + 1}`}</td>
                                          <td className="px-4 py-2 text-sm">
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                              Seed
                                            </span>
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{(investor.shares || 0).toLocaleString()}</td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{percentage}%</td>
                                        </tr>
                                      );
                                    })}
                                    
                                    {/* Series A Investors */}
                                    {capTable.seriesAInvestors?.filter(investor => investor.shares > 0).map((investor, idx) => {
                                      const totalShares = capTable.grandTotal?.shares || 
                                        (capTable.founders?.reduce((sum, f) => sum + (f.shares || 0), 0) || 0) +
                                        (capTable.employeePool?.shares || 0) +
                                        (capTable.seedInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0) +
                                        (capTable.seriesAInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0);
                                      const percentage = totalShares > 0 ? ((investor.shares / totalShares) * 100).toFixed(1) : '0.0';
                                      
                                      // Check if this investor row should be highlighted
                                      const shouldHighlight = dataSource.phase3YellowFlags?.some(flag => 
                                        (flag.includes('Advisor') && flag.includes('>5% equity') && parseFloat(percentage) > 5) ||
                                        (flag.includes('More than 5 investors') && 
                                         (capTable.seedInvestors?.length + capTable.seriesAInvestors?.length) > 5)
                                      );
                                      
                                      return (
                                        <tr key={idx} className={shouldHighlight ? 'bg-yellow-50' : ''}>
                                          <td className="px-4 py-2 text-sm text-gray-900">{investor.name || `Series A Investor ${idx + 1}`}</td>
                                          <td className="px-4 py-2 text-sm">
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                              Series A
                                            </span>
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{(investor.shares || 0).toLocaleString()}</td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{percentage}%</td>
                                        </tr>
                                      );
                                    })}
                                    
                                    {/* Total Row */}
                                    <tr className="bg-gray-50 font-medium border-t-2 border-gray-300">
                                      <td className="px-4 py-2 text-sm text-gray-900">Total</td>
                                      <td className="px-4 py-2 text-sm text-gray-500">-</td>
                                      <td className="px-4 py-2 text-sm text-gray-900">
                                        {(() => {
                                          const totalShares = capTable.grandTotal?.shares || 
                                            (capTable.founders?.reduce((sum, f) => sum + (f.shares || 0), 0) || 0) +
                                            (capTable.employeePool?.shares || 0) +
                                            (capTable.seedInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0) +
                                            (capTable.seriesAInvestors?.reduce((sum, i) => sum + (i.shares || 0), 0) || 0);
                                          return totalShares.toLocaleString();
                                        })()}
                                      </td>
                                      <td className="px-4 py-2 text-sm text-gray-900">100.0%</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Legal Section */}
                      <div className="bg-white border rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                          Part 4: Legal Structure
                        </h4>
                        <div className="space-y-4">
                          {dataSource.incorporated !== undefined && (
                            <div>
                              <label className="text-sm font-bold text-gray-700">Incorporated</label>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.incorporated}</p>
                            </div>
                          )}
                          {dataSource.incorporationLocation && (
                            <div>
                              <label className="text-sm font-bold text-gray-700">Incorporation Location</label>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.incorporationLocation}</p>
                            </div>
                          )}
                          {dataSource.ipAssignment !== undefined && (
                            <div>
                              <div className="flex items-center">
                                {dataSource.phase3YellowFlags?.some(flag => flag.includes('Missing standard venture terms')) && (
                                  <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                  </svg>
                                )}
                                <label className="text-sm font-bold text-gray-700">IP Assignment</label>
                              </div>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.ipAssignment}</p>
                            </div>
                          )}
                          {dataSource.founderVesting !== undefined && (
                            <div>
                              <div className="flex items-center">
                                {dataSource.phase3YellowFlags?.some(flag => flag.includes('Missing standard venture terms')) && (
                                  <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                  </svg>
                                )}
                                <label className="text-sm font-bold text-gray-700">Founder Vesting</label>
                              </div>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.founderVesting}</p>
                            </div>
                          )}
                          {dataSource.founderBoardSeats !== undefined && (
                            <div>
                              <div className="flex items-center">
                                {dataSource.phase3YellowFlags?.some(flag => flag.includes('Missing standard venture terms')) && (
                                  <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                  </svg>
                                )}
                                <label className="text-sm font-bold text-gray-700">Founder Board Seats</label>
                              </div>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.founderBoardSeats}</p>
                            </div>
                          )}
                          {dataSource.openToAmending !== undefined && (
                            <div>
                              <div className="flex items-center">
                                {dataSource.phase3YellowFlags?.some(flag => flag.includes('Not open to amending')) && (
                                  <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                  </svg>
                                )}
                                <label className="text-sm font-bold text-gray-700">Open to Amending Documents</label>
                              </div>
                              <p className="mt-1 text-sm text-gray-900">{dataSource.openToAmending}</p>
                            </div>
                          )}
                        </div>
                        
                        {dataSource.incorporationAgreement && (
                          <div className="mt-4">
                            <div className="flex items-center">
                              {dataSource.phase3YellowFlags?.some(flag => flag.includes('Would like to discuss incorporation')) && (
                                <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                </svg>
                              )}
                              <label className="text-sm font-bold text-gray-700">Incorporation Agreement</label>
                            </div>
                            <p className="mt-1 text-sm text-gray-900">{dataSource.incorporationAgreement}</p>
                          </div>
                        )}
                      </div>

                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="bg-white border rounded-lg p-6">
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-3">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No Phase 3 Data</h4>
                  <p className="text-gray-500">
                    {candidate.currentPhase >= 3 ? 
                      'Phase 3 application data has not been submitted yet.' :
                      'Phase 3 data will appear here once the candidate advances to Phase 3 and submits their in-depth application.'
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Phase 4 Content */}
        {activeTab === 'phase4' && candidate.currentPhase >= 4 && (
          <div className="space-y-6">
            {/* Phase 4 Header */}
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Phase 4: Interview Process</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><strong>Phase 4 Started:</strong> {candidate.phase4AssignedAt ? (candidate.phase4AssignedAt.toDate ? candidate.phase4AssignedAt.toDate().toLocaleDateString() : new Date(candidate.phase4AssignedAt).toLocaleDateString()) : 'Just assigned'}</div>
                <div><strong>Status:</strong> 
                  <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(candidate.status)}`}>
                    {getUnifiedStatus(candidate)}
                  </span>
                </div>
              </div>
            </div>

            {/* Interviewer Information */}
            {candidate.assignedInterviewer && (
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                  Assigned Interviewer
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-bold text-gray-700">Name</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.assignedInterviewer.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-bold text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{candidate.assignedInterviewer.email}</p>
                  </div>
                  {candidate.assignedInterviewer.title && (
                    <div>
                      <label className="text-sm font-bold text-gray-700">Title</label>
                      <p className="mt-1 text-sm text-gray-900">{candidate.assignedInterviewer.title}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Interview Management */}
            <div className="bg-white border rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                Interview Management
              </h4>
              
              {/* Interview Scheduled Checkbox */}
              <div className="mb-6">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={candidate.phase4InterviewScheduled || false}
                    onChange={(e) => updateInterviewScheduled(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Interview Scheduled</span>
                </label>
              </div>

              {/* Interview Document Management */}
              <div>
                <label className="text-sm font-bold text-gray-700 block mb-2">Interview Template Document</label>
                {candidate.phase4DocumentUrl ? (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <a
                        href={candidate.phase4DocumentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <i className="fas fa-external-link-alt mr-2"></i>
                        View Document
                      </a>
                      <button
                        onClick={resetPhase4Document}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <i className="fas fa-times mr-2"></i>
                        Reset
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">Current document: {candidate.phase4DocumentUrl}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <input
                        type="url"
                        value={phase4DocumentInput}
                        onChange={(e) => setPhase4DocumentInput(e.target.value)}
                        placeholder="Enter Google Doc URL..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={setPhase4Document}
                        disabled={!phase4DocumentInput.trim()}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                      >
                        <i className="fas fa-save mr-2"></i>
                        Set Document
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">Set the interview document.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Phase 4 Interviewer Assignment Modal */}
        {showPhase4Modal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96 max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Assign Interviewer for Phase 4
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Select an interviewer to conduct the Phase 4 interview with {candidate?.firstName} {candidate?.lastName}.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interviewer
                </label>
                <select
                  value={selectedInterviewer}
                  onChange={(e) => setSelectedInterviewer(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select an interviewer...</option>
                  {interviewers.map(interviewer => (
                    <option key={interviewer.id} value={interviewer.id}>
                      {interviewer.name} ({interviewer.email})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowPhase4Modal(false);
                    setSelectedInterviewer('');
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={approvePhase4WithInterviewer}
                  disabled={!selectedInterviewer}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  Approve & Assign Interviewer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96 max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Delete Options
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Choose how you want to delete this candidate:
              </p>
              
              <div className="space-y-3 mb-6">
                <button
                  onClick={() => {
                    setDeleteType('entries');
                    confirmDelete();
                  }}
                  disabled={deleting}
                  className="w-full text-left p-3 border border-orange-200 rounded-md hover:bg-orange-50 disabled:opacity-50"
                >
                  <div className="font-medium text-orange-800">Delete All Entries</div>
                  <div className="text-sm text-orange-600 mt-1">
                    Removes all application forms and data, but keeps the user account
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    setDeleteType('user');
                    confirmDelete();
                  }}
                  disabled={deleting}
                  className="w-full text-left p-3 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50"
                >
                  <div className="font-medium text-red-800">Delete All User Data</div>
                  <div className="text-sm text-red-600 mt-1">
                    Removes all application data (Note: Auth account will remain but be unusable)
                  </div>
                </button>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteType(null);
                  }}
                  disabled={deleting}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
              
              {deleting && (
                <div className="mt-4 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                  <span className="ml-2 text-sm text-gray-600">Deleting...</span>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default CandidateProfile;